import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Light/dark theme (§20.1-11, §25.1). Requirement from §7 "Global requirements":
 * the toggle persists, and respects `prefers-color-scheme` on FIRST visit only.
 *
 * Hence two distinct states: an explicit user choice (persisted) versus no choice yet
 * (follow the OS, and keep following it if the OS changes). Collapsing these into a
 * single boolean would either ignore the OS or override a deliberate user choice.
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'visitsj.theme';

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  /** True when following the OS because the user has not chosen explicitly. */
  isSystem: boolean;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const readStoredTheme = (): Theme | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    // Safari private mode / storage disabled — fall back to system preference.
    return null;
  }
};

const systemTheme = (): Theme =>
  window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [explicitTheme, setExplicitTheme] = useState<Theme | null>(readStoredTheme);
  const [osTheme, setOsTheme] = useState<Theme>(systemTheme);

  // Keep following the OS while the user has made no explicit choice.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setOsTheme(event.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const theme = explicitTheme ?? osTheme;

  // Tailwind is configured with darkMode: 'class', so the class on <html> is the switch.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setExplicitTheme(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistence is best-effort; the toggle still works for this session.
    }
  }, []);

  const toggleTheme = useCallback(
    () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    [theme, setTheme],
  );

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme, isSystem: explicitTheme === null }),
    [theme, setTheme, toggleTheme, explicitTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
}
